import styles from "./ResidencyFooter.module.css";
import type { ClassifyDetail } from "../api/client";

/**
 * CS-2 — ResidencyFooter
 *
 * Presentational footer rendered at the bottom of the Classification Station.
 * Exposes two escape paths for an operator who cannot advance a document:
 *
 *   1. Research hold — places the doc in a research_hold residency state.
 *   2. Delete / Flag for deletion — capability-gated:
 *      - canDeleteInClassify === true  → "Delete document" (destructive) → calls
 *        onDeleteDocument, which opens the panel's ConfirmDeleteDialog (with rescan option).
 *      - canDeleteInClassify === false → "Flag for deletion" → calls onFlagDeletion
 *        (defers to server; moves to pending_deletion bucket).
 *
 * When csResidency is already set the current state is shown with a
 * "Resume" / "Unflag" action that calls onClearResidency.
 *
 * This component is PURELY presentational — all mutations are delegated to the
 * passed-in handlers (ClassifyDetailPanel wires them up).
 */

interface ResidencyFooterProps {
  detail: ClassifyDetail;
  /** Called when the operator places a research hold. */
  onResearchHold: (note?: string) => void;
  /** Called when the operator flags (but does not immediately delete) the document.
   *  Only invoked when canDeleteInClassify === false. */
  onFlagDeletion: (note?: string) => void;
  /**
   * Called when the operator clicks "Delete document" (canDeleteInClassify === true).
   * Opens the panel's ConfirmDeleteDialog, which handles confirmation + optional rescan.
   * Optional: only needed when canDeleteInClassify can be true.
   */
  onDeleteDocument?: () => void;
  /** Called when the operator clears/resumes from an active residency. */
  onClearResidency: () => void;
}

/** Human-readable label for a csResidency value. */
function residencyLabel(csResidency: string): string {
  switch (csResidency) {
    case "research_hold":
      return "On hold · research";
    case "pending_deletion":
      return "Pending deletion";
    default:
      return csResidency.replace(/_/g, " ");
  }
}

export function ResidencyFooter({
  detail,
  onResearchHold,
  onFlagDeletion,
  onDeleteDocument,
  onClearResidency,
}: ResidencyFooterProps) {
  const { csResidency, csResidencyNote, canDeleteInClassify } = detail;

  return (
    <div className={styles.footer}>
      {/* ── Active residency banner ─────────────────────────────────────── */}
      {csResidency && (
        <div className={styles.residencyBanner}>
          <div className={styles.residencyInfo}>
            <span className={styles.residencyState}>{residencyLabel(csResidency)}</span>
            {csResidencyNote && (
              <span className={styles.residencyNote}>{csResidencyNote}</span>
            )}
          </div>
          <button
            type="button"
            className={styles.clearBtn}
            onClick={onClearResidency}
            aria-label={
              csResidency === "pending_deletion"
                ? "Unflag — clear pending deletion"
                : "Resume — clear current hold"
            }
          >
            {csResidency === "pending_deletion" ? "Unflag" : "Resume"}
          </button>
        </div>
      )}

      {/* ── Action row ──────────────────────────────────────────────────── */}
      <div className={styles.actions}>
        <span className={styles.sectionLabel}>Residency</span>

        <button
          type="button"
          className={styles.holdBtn}
          onClick={() => onResearchHold()}
          aria-label="Research hold"
        >
          Research hold
        </button>

        {canDeleteInClassify ? (
          <button
            type="button"
            className={styles.dangerBtn}
            onClick={onDeleteDocument}
            aria-label="Delete document"
          >
            Delete document
          </button>
        ) : (
          <button
            type="button"
            className={styles.flagBtn}
            onClick={() => onFlagDeletion()}
            aria-label="Flag for deletion"
          >
            Flag for deletion
          </button>
        )}
      </div>
    </div>
  );
}
